import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  type StripeEnv,
  createStripeClient,
  getStripeErrorMessage,
} from "@/lib/stripe.server";
import { resolveOrCreateCustomer } from "@/lib/subscriptions.server";

export type Subscription = {
  plan: "free" | "premium";
  status: string;
  current_period_end: string | null;
  billing_interval: "month" | "year" | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  price_id: string | null;
  environment: string;
};

export const getMySubscription = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<Subscription> => {
    const { data } = await context.supabase
      .from("subscriptions")
      .select(
        "plan, status, current_period_end, billing_interval, cancel_at_period_end, stripe_customer_id, price_id, environment",
      )
      .eq("user_id", context.userId)
      .maybeSingle();
    return (
      (data as Subscription | null) ?? {
        plan: "free",
        status: "active",
        current_period_end: null,
        billing_interval: null,
        cancel_at_period_end: false,
        stripe_customer_id: null,
        price_id: null,
        environment: "sandbox",
      }
    );
  });

type CheckoutResult = { url: string } | { error: string };


export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (i: {
      priceId: "pro_monthly" | "pro_monthly_usd";
      returnUrl: string;
      cancelUrl?: string;
      environment: StripeEnv;
    }) => {
      if (i.priceId !== "pro_monthly" && i.priceId !== "pro_monthly_usd") {
        throw new Error("Preço inválido");
      }
      return i;
    },
  )

  .handler(async ({ data, context }): Promise<CheckoutResult> => {
    try {
      const stripe = createStripeClient(data.environment);
      const email = (context.claims?.email as string | undefined) ?? undefined;

      const prices = await stripe.prices.list({ lookup_keys: [data.priceId] });
      if (!prices.data.length) throw new Error("Preço não encontrado");
      const stripePrice = prices.data[0];

      const customerId = await resolveOrCreateCustomer(stripe, {
        email,
        userId: context.userId,
      });

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: "subscription",
        success_url: data.returnUrl,
        cancel_url: data.cancelUrl ?? data.returnUrl,
        customer: customerId,
        metadata: { userId: context.userId },
        subscription_data: { metadata: { userId: context.userId } },
      });

      return { url: session.url ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

type PortalResult = { url: string } | { error: string };

export const createPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { returnUrl: string; environment: StripeEnv }) => i)
  .handler(async ({ data, context }): Promise<PortalResult> => {
    try {
      const { data: sub } = await context.supabase
        .from("subscriptions")
        .select("stripe_customer_id")
        .eq("user_id", context.userId)
        .eq("environment", data.environment)
        .maybeSingle();

      let customerId = (sub?.stripe_customer_id as string | null) ?? null;

      if (!customerId) {
        const stripe = createStripeClient(data.environment);
        const email = (context.claims?.email as string | undefined) ?? undefined;
        const found = await stripe.customers.search({
          query: `metadata['userId']:'${context.userId}'`,
          limit: 1,
        });
        customerId = found.data[0]?.id ?? null;
        if (!customerId && email) {
          const list = await stripe.customers.list({ email, limit: 5 });
          customerId =
            list.data.find((c) => c.metadata?.userId === context.userId)?.id ??
            list.data[0]?.id ??
            null;
        }
      }

      if (!customerId) {
        return {
          error: "Você ainda não tem cadastro de pagamento. Inicie um checkout primeiro.",
        };
      }

      const stripe = createStripeClient(data.environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: customerId,
        return_url: data.returnUrl,
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const cancelPremium = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { environment: StripeEnv }) => i)
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub } = await supabaseAdmin
      .from("subscriptions")
      .select("stripe_subscription_id")
      .eq("user_id", context.userId)
      .eq("environment", data.environment)
      .maybeSingle();
    if (!sub?.stripe_subscription_id) {
      await supabaseAdmin
        .from("subscriptions")
        .update({ plan: "free", status: "canceled", current_period_end: null })
        .eq("user_id", context.userId);
      return { ok: true };
    }
    try {
      const stripe = createStripeClient(data.environment);
      await stripe.subscriptions.update(sub.stripe_subscription_id as string, {
        cancel_at_period_end: true,
      });
      await supabaseAdmin
        .from("subscriptions")
        .update({ cancel_at_period_end: true })
        .eq("user_id", context.userId);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: getStripeErrorMessage(error) };
    }
  });

export const resumePremium = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { environment: StripeEnv }) => i)
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub } = await supabaseAdmin
      .from("subscriptions")
      .select("stripe_subscription_id")
      .eq("user_id", context.userId)
      .eq("environment", data.environment)
      .maybeSingle();
    if (!sub?.stripe_subscription_id) return { ok: false, error: "Sem assinatura ativa" };
    try {
      const stripe = createStripeClient(data.environment);
      await stripe.subscriptions.update(sub.stripe_subscription_id as string, {
        cancel_at_period_end: false,
      });
      await supabaseAdmin
        .from("subscriptions")
        .update({ cancel_at_period_end: false })
        .eq("user_id", context.userId);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: getStripeErrorMessage(error) };
    }
  });
